import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  usePlmProgramDetailEditQuery,
  usePlmProgramDetailEditSubSubscription,
  useCreatePlmProgramMutation,
  useUpdatePlmProgramMutation,
  usePlmPartnerOptionsQuery,
  EnumPlmProgramSeason,
  EnumPlmProgramParticipationType,
} from '../../../../types/generated-types';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { Notifier } from '../../../system/services/notificationManager';
import BorderedSection from '../../shared/borderedSection';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import SaveIcon from '@mui/icons-material/Save';

const validationSchema = Yup.object().shape({
  programId: Yup.string().required('Program ID is required'),
  name: Yup.string().required('Name is required'),
  partnerId: Yup.string().required('Partner is required'),
  season: Yup.mixed<EnumPlmProgramSeason>()
    .oneOf(Object.values(EnumPlmProgramSeason))
    .required('Season is required'),
  startDate: Yup.number().required('Start date is required'),
  endDate: Yup.number().nullable(),
  notificationLeadTimeMinutes: Yup.number().nullable(),
  maximumNotificationLeadTimeMinutes: Yup.number().nullable(),
  minimumEventDurationMinutes: Yup.number().nullable(),
  maximumEventDurationMinutes: Yup.number().nullable(),
  minimumEventsPerSeason: Yup.number().nullable(),
  maximumEventsPerSeason: Yup.number().nullable(),
  measurementIntervalMinutes: Yup.number().nullable(),
  participationType: Yup.mixed<EnumPlmProgramParticipationType>()
    .oneOf(Object.values(EnumPlmProgramParticipationType))
    .required('Participation type is required'),
  incentiveDescription: Yup.string().nullable(),
  hostPartnerId: Yup.string().nullable(),
});

interface PlmProgramFormValues {
  programId: string;
  name: string;
  partnerId: string;
  hostPartnerId?: string;
  season: EnumPlmProgramSeason;
  startDate: number;
  endDate?: number;
  notificationLeadTimeMinutes?: number;
  maximumNotificationLeadTimeMinutes?: number;
  minimumEventDurationMinutes?: number;
  maximumEventDurationMinutes?: number;
  minimumEventsPerSeason?: number;
  maximumEventsPerSeason?: number;
  measurementIntervalMinutes?: number;
  participationType: EnumPlmProgramParticipationType;
  incentiveDescription?: string;
}

export const PlmProgramDetailForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { can } = useAuthorizer();
  const isEditMode = !!id;

  const { data, loading, error } = usePlmProgramDetailEditQuery({
    variables: { id: id! },
    fetchPolicy: 'network-only',
    skip: !isEditMode,
  });

  usePlmProgramDetailEditSubSubscription({
    variables: { id: id! },
    skip: !isEditMode,
  });

  const [createPlmProgram] = useCreatePlmProgramMutation();
  const [updatePlmProgram] = useUpdatePlmProgramMutation();

  const { data: partnerOptionsData, loading: partnerOptionsLoading } =
    usePlmPartnerOptionsQuery();

  const plmProgram = data?.plmProgramById;

  const initialValues: PlmProgramFormValues = {
    programId: plmProgram?.programId || '',
    name: plmProgram?.name || '',
    partnerId: plmProgram?.partnerId || '',
    hostPartnerId: plmProgram?.hostPartnerId || '',
    season:
      (plmProgram?.season as EnumPlmProgramSeason) ||
      EnumPlmProgramSeason.Summer,
    startDate: plmProgram?.startDate || Date.now(),
    endDate: plmProgram?.endDate || undefined,
    notificationLeadTimeMinutes:
      plmProgram?.notificationLeadTimeMinutes || undefined,
    maximumNotificationLeadTimeMinutes:
      plmProgram?.maximumNotificationLeadTimeMinutes || undefined,
    minimumEventDurationMinutes:
      plmProgram?.minimumEventDurationMinutes || undefined,
    maximumEventDurationMinutes:
      plmProgram?.maximumEventDurationMinutes || undefined,
    minimumEventsPerSeason: plmProgram?.minimumEventsPerSeason || undefined,
    maximumEventsPerSeason: plmProgram?.maximumEventsPerSeason || undefined,
    measurementIntervalMinutes:
      plmProgram?.measurementIntervalMinutes || undefined,
    participationType:
      (plmProgram?.participationType as EnumPlmProgramParticipationType) ||
      EnumPlmProgramParticipationType.OptIn,
    incentiveDescription: plmProgram?.incentiveDescription || undefined,
  };

  const handleSubmit = async (values: PlmProgramFormValues) => {
    try {
      if (isEditMode) {
        if (!can('update', 'PlmProgram')) {
          Notifier.error('You do not have permission to update PLM programs');
          return;
        }

        await updatePlmProgram({
          variables: {
            id: id!,
            plmProgram: {
              ...values,
              hostPartnerId: values.hostPartnerId || null,
            },
          },
        });

        Notifier.success('PLM program updated successfully');
      } else {
        if (!can('create', 'PlmProgram')) {
          Notifier.error('You do not have permission to create PLM programs');
          return;
        }

        await createPlmProgram({
          variables: {
            plmProgram: {
              ...values,
              hostPartnerId: values.hostPartnerId || null,
            },
          },
        });

        Notifier.success('PLM program created successfully');
      }

      setTimeout(() => {
        navigate('/plmPrograms');
      }, 1500);
    } catch (error) {
      Notifier.error(
        `Failed to ${isEditMode ? 'update' : 'create'} PLM program: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  };

  const handleCancel = () => {
    navigate('/plmPrograms');
  };

  const isSmall = useIsSmallScreen();

  if (loading || partnerOptionsLoading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          Error loading PLM program: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" mb={4} gutterBottom>
        {isEditMode
          ? isSmall
            ? 'Edit PLM Program'
            : 'Edit Peak Load Management Program'
          : isSmall
            ? 'Add PLM Program'
            : 'Create Peak Load Management Program'}
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <BorderedSection title="Basic Information">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="programId"
                    label="Program ID"
                    value={values.programId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.programId && !!errors.programId}
                    helperText={touched.programId && errors.programId}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.partnerId && !!errors.partnerId}
                  >
                    <InputLabel>Partner</InputLabel>
                    <Select
                      name="partnerId"
                      value={values.partnerId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Partner"
                    >
                      <MenuItem value="">
                        <em>Select a partner</em>
                      </MenuItem>
                      {partnerOptionsData?.plmPartners?.map((partner) => (
                        <MenuItem key={partner._id} value={partner._id}>
                          {partner.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.hostPartnerId && !!errors.hostPartnerId}
                  >
                    <InputLabel>
                      Host / settlement partner (optional)
                    </InputLabel>
                    <Select
                      name="hostPartnerId"
                      value={values.hostPartnerId ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Host / settlement partner (optional)"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {partnerOptionsData?.plmPartners?.map((partner) => (
                        <MenuItem key={partner._id} value={partner._id}>
                          {partner.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.season && !!errors.season}
                  >
                    <InputLabel>Season</InputLabel>
                    <Select
                      name="season"
                      value={values.season}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Season"
                    >
                      {Object.values(EnumPlmProgramSeason).map((season) => (
                        <MenuItem key={season} value={season}>
                          {season}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="startDate"
                    label="Start Date"
                    type="datetime-local"
                    value={new Date(values.startDate)
                      .toISOString()
                      .slice(0, 16)}
                    onChange={(e) =>
                      setFieldValue(
                        'startDate',
                        new Date(e.target.value).getTime(),
                      )
                    }
                    onBlur={handleBlur}
                    error={touched.startDate && !!errors.startDate}
                    helperText={touched.startDate && errors.startDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="endDate"
                    label="End Date"
                    type="datetime-local"
                    value={
                      values.endDate
                        ? new Date(values.endDate).toISOString().slice(0, 16)
                        : ''
                    }
                    onChange={(e) =>
                      setFieldValue(
                        'endDate',
                        e.target.value
                          ? new Date(e.target.value).getTime()
                          : undefined,
                      )
                    }
                    onBlur={handleBlur}
                    error={touched.endDate && !!errors.endDate}
                    helperText={touched.endDate && errors.endDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={
                      touched.participationType && !!errors.participationType
                    }
                  >
                    <InputLabel>Participation Type</InputLabel>
                    <Select
                      name="participationType"
                      value={values.participationType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Participation Type"
                    >
                      {Object.values(EnumPlmProgramParticipationType).map(
                        (type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="incentiveDescription"
                    label="Incentive"
                    value={values.incentiveDescription || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.incentiveDescription &&
                      !!errors.incentiveDescription
                    }
                    helperText={
                      touched.incentiveDescription &&
                      errors.incentiveDescription
                    }
                  />
                </Grid>
              </Grid>
            </BorderedSection>

            <BorderedSection title="Event Configuration">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="notificationLeadTimeMinutes"
                    label="Notification Lead Time (minutes)"
                    type="number"
                    value={values.notificationLeadTimeMinutes || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.notificationLeadTimeMinutes &&
                      !!errors.notificationLeadTimeMinutes
                    }
                    helperText={
                      touched.notificationLeadTimeMinutes &&
                      errors.notificationLeadTimeMinutes
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="maximumNotificationLeadTimeMinutes"
                    label="Maximum Notification Lead Time (minutes)"
                    type="number"
                    value={values.maximumNotificationLeadTimeMinutes || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.maximumNotificationLeadTimeMinutes &&
                      !!errors.maximumNotificationLeadTimeMinutes
                    }
                    helperText={
                      touched.maximumNotificationLeadTimeMinutes &&
                      errors.maximumNotificationLeadTimeMinutes
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="minimumEventDurationMinutes"
                    label="Minimum Event Duration (minutes)"
                    type="number"
                    value={values.minimumEventDurationMinutes || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.minimumEventDurationMinutes &&
                      !!errors.minimumEventDurationMinutes
                    }
                    helperText={
                      touched.minimumEventDurationMinutes &&
                      errors.minimumEventDurationMinutes
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="maximumEventDurationMinutes"
                    label="Maximum Event Duration (minutes)"
                    type="number"
                    value={values.maximumEventDurationMinutes || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.maximumEventDurationMinutes &&
                      !!errors.maximumEventDurationMinutes
                    }
                    helperText={
                      touched.maximumEventDurationMinutes &&
                      errors.maximumEventDurationMinutes
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="minimumEventsPerSeason"
                    label="Minimum Events Per Season"
                    type="number"
                    value={values.minimumEventsPerSeason || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.minimumEventsPerSeason &&
                      !!errors.minimumEventsPerSeason
                    }
                    helperText={
                      touched.minimumEventsPerSeason &&
                      errors.minimumEventsPerSeason
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="maximumEventsPerSeason"
                    label="Maximum Events Per Season"
                    type="number"
                    value={values.maximumEventsPerSeason || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.maximumEventsPerSeason &&
                      !!errors.maximumEventsPerSeason
                    }
                    helperText={
                      touched.maximumEventsPerSeason &&
                      errors.maximumEventsPerSeason
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="measurementIntervalMinutes"
                    label="Measurement Interval (minutes)"
                    type="number"
                    value={values.measurementIntervalMinutes || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.measurementIntervalMinutes &&
                      !!errors.measurementIntervalMinutes
                    }
                    helperText={
                      touched.measurementIntervalMinutes &&
                      errors.measurementIntervalMinutes
                    }
                  />
                </Grid>
              </Grid>
            </BorderedSection>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  startIcon={<SaveIcon />}
                  disabled={
                    !can(isEditMode ? 'update' : 'create', 'PlmProgram')
                  }
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
